"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "nextjs-toploader/app";
import { useQueryState } from "nuqs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DefaultAvatar from "@/assets/images/account/default-avatar.png";
import UserAvatar from "@/assets/svg/user-avatar.svg";
import { PopLoading } from "@/components/pop-ui/pop-loading";
import { ModuleDrawer } from "@/components/ui/module-drawer";
import { ModuleSheet } from "@/components/ui/module-sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { authClient } from "@/lib/auth/auth-client";
import LocaleLink from "@/lib/i18n/locale-link";
import { cn } from "@/lib/utils";
import { useORPC } from "@/providers/orpc-provider";
import { AddressesModule } from "./account-module-addresses";
import { CouponsModule } from "./account-module-coupons";
import { ExchangeModule } from "./account-module-exchange";
import { OrdersModule } from "./account-module-orders";
import { OrdersStoreGoModule } from "./account-module-orders-store-go";
import { PointsModule } from "./account-module-points";
import { RegistrationsModule } from "./account-module-registrations";
import { RewardsModule } from "./account-module-rewards";
import { UserAccountSidebar } from "./user-account-sidebar";
export function UserAccountInHeader(t0) {
  const $ = _c(13);
  const {
    showLabel: t1,
    onClick
  } = t0;
  const showLabel = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const {
    data: session,
    isPending
  } = authClient.useSession();
  const userProfile = session?.user;
  const isLoggedIn = !!userProfile && userProfile.isAnonymous !== true;
  userProfile?.image || DefaultAvatar;
  const href = isLoggedIn ? "/account" : "/user/login?redirect=/account";
  if (isPending) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <UserAvatar className="size-5.5 md:h-[24px] md:w-[24px]" />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="hidden">My Account</span>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <UserAvatar className="size-5.5 md:h-[24px] md:w-[24px]" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isLoggedIn || $[4] !== showLabel || $[5] !== t) {
    t4 = showLabel && <span className="text-xs underline md:hidden">{t(isLoggedIn ? "header.myAccount" : "header.signInOrRegister")}</span>;
    $[3] = isLoggedIn;
    $[4] = showLabel;
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <div className="flex items-center gap-2">{t3}{t4}</div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== href || $[10] !== onClick || $[11] !== t5) {
    t6 = <LocaleLink href={href} className="flex cursor-pointer items-center justify-center gap-1 text-xs md:text-sm" onClick={onClick}>{t2}{t5}</LocaleLink>;
    $[9] = href;
    $[10] = onClick;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
interface ModuleRendererProps {
  activeModule: string;
  isMobile: boolean;
  isMemberUser: boolean;
  onConfirmDialogStateChange: (state: boolean) => void;
  onGoRewards: () => void;
  onCouponsHeaderContentChange?: (content: React.ReactNode) => void;
  onRewardsHeaderContentChange?: (content: React.ReactNode) => void;
}
const ModuleRenderer = t0 => {
  const $ = _c(17);
  const {
    activeModule,
    isMobile,
    isMemberUser,
    onConfirmDialogStateChange,
    onGoRewards,
    onCouponsHeaderContentChange,
    onRewardsHeaderContentChange
  } = t0;
  switch (activeModule) {
    case "orders":
      {
        let t1;
        if ($[0] !== onConfirmDialogStateChange) {
          t1 = <OrdersModule onConfirmDialogStateChange={onConfirmDialogStateChange} />;
          $[0] = onConfirmDialogStateChange;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "orders-store-go":
      {
        let t1;
        if ($[2] !== onConfirmDialogStateChange) {
          t1 = <OrdersStoreGoModule onConfirmDialogStateChange={onConfirmDialogStateChange} />;
          $[2] = onConfirmDialogStateChange;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case "addresses":
      {
        let t1;
        if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <AddressesModule />;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
    case "exchange":
      {
        let t1;
        if ($[5] !== isMemberUser || $[6] !== onGoRewards) {
          t1 = <ExchangeModule onGoRewards={onGoRewards} isMemberUser={isMemberUser} />;
          $[5] = isMemberUser;
          $[6] = onGoRewards;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
    case "rewards":
      {
        let t1;
        if ($[8] !== onRewardsHeaderContentChange) {
          t1 = <RewardsModule onHeaderContentChange={onRewardsHeaderContentChange} />;
          $[8] = onRewardsHeaderContentChange;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        return t1;
      }
    case "registrations":
      {
        let t1;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <RegistrationsModule />;
          $[10] = t1;
        } else {
          t1 = $[10];
        }
        return t1;
      }
    case "points":
      {
        let t1;
        if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <PointsModule />;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        return t1;
      }
    case "coupons":
      {
        let t1;
        if ($[12] !== onCouponsHeaderContentChange) {
          t1 = <CouponsModule onHeaderContentChange={onCouponsHeaderContentChange} />;
          $[12] = onCouponsHeaderContentChange;
          $[13] = t1;
        } else {
          t1 = $[13];
        }
        return t1;
      }
    case "profile":
    case "settings":
      {
        return null;
      }
    default:
      {
        let t1;
        if ($[14] !== isMobile || $[15] !== onConfirmDialogStateChange) {
          t1 = isMobile ? null : <OrdersModule onConfirmDialogStateChange={onConfirmDialogStateChange} />;
          $[14] = isMobile;
          $[15] = onConfirmDialogStateChange;
          $[16] = t1;
        } else {
          t1 = $[16];
        }
        return t1;
      }
  }
};
export function UserAccount() {
  const $ = _c(69);
  const {
    t
  } = useTranslation();
  const isMobile = useIsMobile();
  const [clientSideCheckDone, setClientSideCheckDone] = useState(false);
  const [couponsHeaderContent, setCouponsHeaderContent] = useState(null);
  const [rewardsHeaderContent, setRewardsHeaderContent] = useState(null);
  const queryClient = useQueryClient();
  const orpc = useORPC();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      defaultValue: "",
      parse: _temp,
      history: "push"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [activeModule, setActiveModule] = useQueryState("tab", t0);
  const router = useRouter();
  const {
    data: session
  } = authClient.useSession();
  const isMemberUser = session?.user?.isAnonymous === false;
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const confirmDialogOpenRef = React.useRef(false);
  let t1;
  if ($[1] !== activeModule || $[2] !== isMobile) {
    t1 = isMobile && activeModule && !["profile", "settings"].includes(activeModule);
    $[1] = activeModule;
    $[2] = isMobile;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const drawerOpen = t1;
  let t2;
  let t3;
  if ($[4] !== activeModule || $[5] !== clientSideCheckDone || $[6] !== isMobile || $[7] !== setActiveModule) {
    t2 = () => {
      if (!isMobile && !activeModule && clientSideCheckDone) {
        setActiveModule("orders");
      }
    };
    t3 = [isMobile, activeModule, setActiveModule, clientSideCheckDone];
    $[4] = activeModule;
    $[5] = clientSideCheckDone;
    $[6] = isMobile;
    $[7] = setActiveModule;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== activeModule) {
    t4 = [activeModule];
    $[10] = activeModule;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(_temp2, t4);
  let t5;
  if ($[12] !== activeModule || $[13] !== clientSideCheckDone || $[14] !== orpc || $[15] !== queryClient) {
    t5 = () => {
      if (!clientSideCheckDone) {
        return;
      }
      const isDefaultState = activeModule === "";
      if (isDefaultState) {
        queryClient.invalidateQueries({
          queryKey: orpc.member.getCurrentPoints.queryOptions({
            input: {}
          }).queryKey
        });
        queryClient.invalidateQueries({
          queryKey: orpc.ec.userCoupon.findMyCouponCounts.queryOptions({
            input: {}
          }).queryKey
        });
      }
    };
    $[12] = activeModule;
    $[13] = clientSideCheckDone;
    $[14] = orpc;
    $[15] = queryClient;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== activeModule || $[18] !== clientSideCheckDone || $[19] !== isMobile || $[20] !== orpc || $[21] !== queryClient) {
    t6 = [activeModule, isMobile, clientSideCheckDone, queryClient, orpc];
    $[17] = activeModule;
    $[18] = clientSideCheckDone;
    $[19] = isMobile;
    $[20] = orpc;
    $[21] = queryClient;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setClientSideCheckDone(true);
    };
    t8 = [];
    $[23] = t7;
    $[24] = t8;
  } else {
    t7 = $[23];
    t8 = $[24];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[25] !== activeModule || $[26] !== router) {
    t9 = () => {
      if (activeModule === "profile") {
        router.push("/account/manage");
      } else {
        if (activeModule === "settings") {
          router.push("/account/setting");
        }
      }
    };
    t10 = [activeModule, router];
    $[25] = activeModule;
    $[26] = router;
    $[27] = t10;
    $[28] = t9;
  } else {
    t10 = $[27];
    t9 = $[28];
  }
  useEffect(t9, t10);
  if (!clientSideCheckDone) {
    let t11;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="container flex min-h-[500px] items-center justify-center py-10"><PopLoading /></div>;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    return t11;
  }
  let t11;
  if ($[30] !== isMobile || $[31] !== router || $[32] !== setActiveModule) {
    t11 = module => {
      if (isMobile) {
        if (module === "profile" || module === "settings") {
          if (module === "profile") {
            router.push("/account/manage");
          } else {
            router.push("/account/setting");
          }
          return;
        }
        setActiveModule(module);
      } else {
        setActiveModule(module);
      }
    };
    $[30] = isMobile;
    $[31] = router;
    $[32] = setActiveModule;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const handleModuleSelect = t11;
  let t12;
  if ($[34] !== isConfirmDialogOpen || $[35] !== setActiveModule) {
    t12 = open => {
      if (!open && !isConfirmDialogOpen && !confirmDialogOpenRef.current) {
        setActiveModule("");
      }
    };
    $[34] = isConfirmDialogOpen;
    $[35] = setActiveModule;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const handleDrawerClose = t12;
  let t13;
  if ($[37] !== t) {
    t13 = module_0 => {
      switch (module_0) {
        case "orders":
          {
            return t("account.orders.title", "\u6211\u7684\u8A02\u55AE");
          }
        case "orders-store-go":
          {
            return t("account.orders.storeGo.title", "\u9580\u5E97\u53D6\u8CA8");
          }
        case "addresses":
          {
            return t("account.addresses.title", "\u5730\u5740\u7C3F");
          }
        case "exchange":
          {
            return t("account.exchange.title", "\u5956\u52B1\u5151\u6362");
          }
        case "rewards":
          {
            return t("account.rewards.title", "\u8CFA\u53D6\u7684\u734E\u52F5");
          }
        case "registrations":
          {
            return t("account.registrations.title", "\u6211\u7684\u9810\u7D04");
          }
        case "points":
          {
            return t("account.points.title", "\u7A4D\u5206");
          }
        case "coupons":
          {
            return t("account.coupons.title", "\u512A\u60E0\u5238");
          }
        default:
          {
            return "";
          }
      }
    };
    $[37] = t;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const getModuleTitle = t13;
  if (isMobile) {
    let t14;
    if ($[39] !== activeModule || $[40] !== handleModuleSelect) {
      t14 = <div className="container py-4"><UserAccountSidebar activeModule={activeModule} setActiveModule={handleModuleSelect} isMobile={true} /></div>;
      $[39] = activeModule;
      $[40] = handleModuleSelect;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    let t15;
    if ($[42] !== activeModule || $[43] !== couponsHeaderContent || $[44] !== drawerOpen || $[45] !== getModuleTitle || $[46] !== handleDrawerClose || $[47] !== isMemberUser || $[48] !== isMobile || $[49] !== rewardsHeaderContent || $[50] !== setActiveModule) {
      t15 = activeModule === "orders" || activeModule === "coupons" ? <ModuleSheet open={!!drawerOpen} onOpenChange={handleDrawerClose} title={getModuleTitle(activeModule)} rightContent={activeModule === "coupons" ? couponsHeaderContent : activeModule === "rewards" ? rewardsHeaderContent : undefined} disableAnimation={activeModule === "orders"} disableDrag={true}><ModuleRenderer activeModule={activeModule} isMobile={isMobile} isMemberUser={isMemberUser} onConfirmDialogStateChange={state => {
          setIsConfirmDialogOpen(state);
          confirmDialogOpenRef.current = state;
        }} onGoRewards={() => setActiveModule("rewards")} onCouponsHeaderContentChange={setCouponsHeaderContent} /></ModuleSheet> : <ModuleDrawer open={!!drawerOpen} onOpenChange={handleDrawerClose} title={getModuleTitle(activeModule)} rightContent={activeModule === "coupons" ? couponsHeaderContent : activeModule === "rewards" ? rewardsHeaderContent : undefined} contentClassName={cn(["rewards"].includes(activeModule) && "!pt-0", ["points", "exchange", "orders-store-go", "registrations"].includes(activeModule) && "!p-0", ["orders-store-go"].includes(activeModule) && "bg-muted md:bg-white")} disableDrag={true}><ModuleRenderer activeModule={activeModule} isMobile={isMobile} isMemberUser={isMemberUser} onConfirmDialogStateChange={state_0 => {
          setIsConfirmDialogOpen(state_0);
          confirmDialogOpenRef.current = state_0;
        }} onGoRewards={() => setActiveModule("rewards")} onCouponsHeaderContentChange={setCouponsHeaderContent} onRewardsHeaderContentChange={setRewardsHeaderContent} /></ModuleDrawer>;
      $[42] = activeModule;
      $[43] = couponsHeaderContent;
      $[44] = drawerOpen;
      $[45] = getModuleTitle;
      $[46] = handleDrawerClose;
      $[47] = isMemberUser;
      $[48] = isMobile;
      $[49] = rewardsHeaderContent;
      $[50] = setActiveModule;
      $[51] = t15;
    } else {
      t15 = $[51];
    }
    let t16;
    if ($[52] !== t14 || $[53] !== t15) {
      t16 = <>{t14}{t15}</>;
      $[52] = t14;
      $[53] = t15;
      $[54] = t16;
    } else {
      t16 = $[54];
    }
    return t16;
  }
  let t14;
  if ($[55] !== activeModule || $[56] !== isMemberUser || $[57] !== setActiveModule) {
    t14 = isMemberUser ? <aside className="sticky top-[100px] w-342/1200 max-w-342/1440 min-w-[200px] self-start"><UserAccountSidebar activeModule={activeModule} setActiveModule={setActiveModule} isMobile={false} /></aside> : null;
    $[55] = activeModule;
    $[56] = isMemberUser;
    $[57] = setActiveModule;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== setActiveModule) {
    t15 = () => setActiveModule("rewards");
    $[59] = setActiveModule;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] !== activeModule || $[62] !== isMemberUser || $[63] !== isMobile || $[64] !== t15) {
    t16 = <main className="mx-auto max-w-1074/1440 flex-1"><ModuleRenderer activeModule={activeModule} isMobile={isMobile} isMemberUser={isMemberUser} onConfirmDialogStateChange={setIsConfirmDialogOpen} onGoRewards={t15} onCouponsHeaderContentChange={setCouponsHeaderContent} onRewardsHeaderContentChange={setRewardsHeaderContent} /></main>;
    $[61] = activeModule;
    $[62] = isMemberUser;
    $[63] = isMobile;
    $[64] = t15;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  let t17;
  if ($[66] !== t14 || $[67] !== t16) {
    t17 = <div className="3xl:gap-6 relative container flex w-full gap-4 py-15 xl:gap-4 2xl:gap-5">{t14}{t16}</div>;
    $[66] = t14;
    $[67] = t16;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  return t17;
}
function _temp2() {
  window.scrollTo({
    top: 0,
    behavior: "auto"
  });
}
function _temp(v) {
  return v;
}